import { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import instanciaAxios from '../miscelaneo/instanciaAxios';
import { roundTo2 } from '../miscelaneo/monetario';

function obtenerFechaHoraActualLocal() {
  const ahora = new Date();
  const offset = ahora.getTimezoneOffset();
  const fechaLocal = new Date(ahora.getTime() - offset * 60000);
  return fechaLocal.toISOString().slice(0, 16);
}

function obtenerFechaActualLocal() {
  const ahora = new Date();
  const offset = ahora.getTimezoneOffset();
  const fechaLocal = new Date(ahora.getTime() - offset * 60000);
  return fechaLocal.toISOString().slice(0, 10);
}

function resolverNombreClienteDisplay(cliente = {}) {
  const razonSocial = String(cliente.razon_social || cliente.razonSocial || '').trim();
  if (razonSocial) return razonSocial;

  const nombre = String(cliente.nombre || '').trim();
  const apellido = String(cliente.apellido || '').trim();
  const nombreCompleto = [nombre, apellido].filter(Boolean).join(' ').trim();

  return nombreCompleto || '';
}

function normalizarIdListaPrecio(valor) {
  if (valor === undefined || valor === null || valor === '') return null;
  const id = Number.parseInt(valor, 10);
  return Number.isInteger(id) && id > 0 ? id : null;
}

function parsearMonto(valor, fallback = 0) {
  const numero = parseFloat(valor);
  return Number.isFinite(numero) ? numero : fallback;
}

/**
 * Tasa de ITBIS efectiva de un producto para fines de cálculo/edición de
 * precio: la misma que aplicará calcularImpuestoPorProducto al facturar
 * (0 si el indicador de facturación no es 1/2/3, aunque el producto tenga
 * itbis_tasa cargado). Se usa tanto para mostrarla (solo lectura) en el
 * modal de producto como para el cálculo inverso total → precio base.
 */
function obtenerTasaItbisEfectiva(producto) {
  if (!producto) return 0;
  const indicador = producto.indicador_facturacion || 1;
  if (![1, 2, 3].includes(indicador)) return 0;

  let tasa = indicador === 2 ? 16 : 18;
  if (producto.itbis_tasa !== undefined && producto.itbis_tasa !== null) {
    tasa = producto.itbis_tasa;
  }
  return tasa;
}

/**
 * Hook compartido para gestión de tickets de facturación.
 * Usado por ProcesoPOS y ProcesoFacturaDemo.
 * 
 * @param {Object} options
 * @param {string} options.defaultTipoComprobante - Tipo comprobante por defecto ('2' para POS, null para Demo)
 * @returns {Object} Estado y funciones de gestión de tickets
 */
export default function useTicketManager({ defaultTipoComprobante = '2' } = {}) {
  // Estado principal
  const [tickets, setTickets] = useState([
    {
      id: 1,
      nombre: 'Ticket 1',
      productos: [],
      totales: { subtotal: 0, itbis: 0, total: 0 },
      clienteSeleccionado: null,
      nombreCliente: '',
      id_lista_precio: null,
      tipoComprobanteFiscal: defaultTipoComprobante,
      comentario: '',
      fecha_hora_creacion: obtenerFechaHoraActualLocal(),
      fecha_emision: obtenerFechaActualLocal(),
      id_centro_costo: null,
      tipoPago: 'contado',
      metodosPago: [{ metodoPago: '01', monto: '' }],
      abonoInicial: null,
      // Edit mode fields (for FacturaDemo)
      id_factura_editando: null,
      productosOriginales: null,
      id_entidad_cliente_original: null
    }
  ]);
  const [ticketActivo, setTicketActivo] = useState(0);
  const [sucursales, setSucursales] = useState([]);
  const [sucursalSeleccionada, setSucursalSeleccionada] = useState('');
  const [centrosCosto, setCentrosCosto] = useState([]);
  const [mostrarCamposAdicionales, setMostrarCamposAdicionales] = useState(false);
  const [mostrarModalClientes, setMostrarModalClientes] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [procesandoCobro, setProcesandoCobro] = useState(false);
  const [tiposComprobantesFiscales, setTiposComprobantesFiscales] = useState([]);
  const [mostrarModalNuevoCliente, setMostrarModalNuevoCliente] = useState(false);
  
  // Modal para cantidad de producto
  const [mostrarModalCantidad, setMostrarModalCantidad] = useState(false);
  const [productoTemporal, setProductoTemporal] = useState(null);
  const [cantidadTemporal, setCantidadTemporal] = useState('1');
  const [precioTemporal, setPrecioTemporal] = useState('');
  const [modoModal, setModoModal] = useState('agregar');
  // Modo de edición de precio dentro del modal de producto: 'unitario' edita
  // el precio base directamente (comportamiento histórico); 'total' permite
  // digitar el total con ITBIS incluido y calcula el precio base a partir de
  // ese total (mismo enfoque de MantenimientoProducto: total / (1 + tasa/100)).
  const [modoPrecioTemporal, setModoPrecioTemporal] = useState('unitario');
  const [totalTemporalConImpuesto, setTotalTemporalConImpuesto] = useState('');
  const [indexProductoEditar, setIndexProductoEditar] = useState(null);
  
  // Modal de pago
  const [mostrarModalPago, setMostrarModalPago] = useState(false);
  
  // Estados para PDF y factura creada
  const [mostrarPDFFactura, setMostrarPDFFactura] = useState(false);
  const [pdfBase64, setPdfBase64] = useState(null);
  const [facturaCreada, setFacturaCreada] = useState(null);
  
  // Métodos de pago (catálogo)
  const [metodosPago, setMetodosPago] = useState([]);

  // Defaults del sistema
  const [clienteDefecto, setClienteDefecto] = useState(null);
  const [pasarelaDefecto, setPasarelaDefecto] = useState(null);
  const [sucursalDefecto, setSucursalDefecto] = useState(null);

  // Búsqueda de facturas
  const [mostrarModalBusquedaFacturas, setMostrarModalBusquedaFacturas] = useState(false);
  const [facturasBuscadas, setFacturasBuscadas] = useState([]);
  const [cargandoFacturas, setCargandoFacturas] = useState(false);
  const [idFacturaPDF, setIdFacturaPDF] = useState(null);
  const [filtrosBusqueda, setFiltrosBusqueda] = useState({
    busqueda: '',
    fecha_desde: '',
    fecha_hasta: ''
  });
  const [confirmacionCambioLista, setConfirmacionCambioLista] = useState({
    mostrar: false,
    titulo: 'Actualizar precios por lista',
    mensaje: '',
    textoConfirmar: 'Recalcular',
    textoCancelar: 'Mantener',
    tipo: 'info'
  });

  // Referencias
  const inputProductoRef = useRef();
  const inputCantidadRef = useRef();
  const resolverConfirmacionListaRef = useRef(null);
  const datosInicialesCargadosRef = useRef(false);

  // Cargar datos iniciales
  useEffect(() => {
    // Evita doble ejecución en React.StrictMode (desarrollo), que de otro modo
    // dispara dos fetch en paralelo y el segundo puede sobrescribir el cliente
    // por defecto sobre datos ya cargados (p. ej. la precarga de una cotización).
    if (datosInicialesCargadosRef.current) return;
    datosInicialesCargadosRef.current = true;

    const fetchData = async () => {
      try {
        setCargando(true);
        const [sucursalesResp, tiposComprobantesResp, centrosCostoResp, metodosPagoResp, configuracionResp] = await Promise.all([
          instanciaAxios.get('/miscelaneo/sucursal'),
          instanciaAxios.get('/comprobantefiscal/tipo'),
          instanciaAxios.get('/miscelaneo/centro_costo'),
          instanciaAxios.get('/miscelaneo/metodo_pago'),
          instanciaAxios.get('/miscelaneo/configuracion').catch(() => ({ data: {} }))
        ]);
        setSucursales(sucursalesResp.data);
        setTiposComprobantesFiscales(tiposComprobantesResp.data);
        setCentrosCosto(centrosCostoResp.data);
        setMetodosPago(metodosPagoResp.data);

        const configData = configuracionResp.data;
        if (configData?.id_entidad_cliente_defecto) {
          const nombreCliente = configData.nombre_cliente || configData.razon_social_cliente || '';
          let idListaPrecioDefecto = null;
          let tipoComprobanteDefecto = defaultTipoComprobante;

          try {
            const detalleClienteResp = await instanciaAxios.get(`/entidad/${configData.id_entidad_cliente_defecto}`);
            const detallesCliente = detalleClienteResp?.data?.detallesCliente || {};
            idListaPrecioDefecto = normalizarIdListaPrecio(detallesCliente.id_lista_precio);
            if (detallesCliente.id_tipo_comprobante_fiscal) {
              tipoComprobanteDefecto = detallesCliente.id_tipo_comprobante_fiscal.toString();
            }
          } catch (errorDetalleCliente) {
            console.warn('No fue posible cargar detalle de cliente por defecto:', errorDetalleCliente);
          }

          setClienteDefecto({
            id: configData.id_entidad_cliente_defecto,
            nombre: nombreCliente,
            id_lista_precio: idListaPrecioDefecto
          });
          setTickets(prev => {
            const nuevos = [...prev];
            nuevos[0] = {
              ...nuevos[0],
              clienteSeleccionado: configData.id_entidad_cliente_defecto,
              nombreCliente: nombreCliente,
              id_lista_precio: idListaPrecioDefecto,
              tipoComprobanteFiscal: tipoComprobanteDefecto,
              id_entidad_cliente_original: configData.id_entidad_cliente_defecto
            };
            return nuevos;
          });
        }

        if (configData?.id_pasarela_pago_defecto) {
          setPasarelaDefecto(configData.id_pasarela_pago_defecto);
        }

        if (configData?.id_sucursal_defecto) {
          setSucursalDefecto(configData.id_sucursal_defecto);
          setSucursalSeleccionada(String(configData.id_sucursal_defecto));
        }
      } catch (error) {
        console.error('Error al cargar datos iniciales:', error);
        toast.error('Error al cargar datos iniciales');
      } finally {
        setCargando(false);
      }
    };
    fetchData();
  }, []);

  // Atajos de teclado para cambiar tickets
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key >= '1' && e.key <= '9') {
        const index = parseInt(e.key) - 1;
        if (index < tickets.length) {
          setTicketActivo(index);
          e.preventDefault();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [tickets]);

  // Focus automático en input de producto al cambiar ticket
  useEffect(() => {
    if (inputProductoRef.current) {
      setTimeout(() => {
        inputProductoRef.current?.limpiarYFocus?.();
      }, 100);
    }
  }, [ticketActivo]);

  // Restaurar focus después de que el diálogo de impresión del navegador se cierre
  useEffect(() => {
    const handlePrintDone = () => {
      setTimeout(() => {
        inputProductoRef.current?.focus?.();
      }, 150);
    };
    window.addEventListener('omnierp:print-done', handlePrintDone);
    return () => window.removeEventListener('omnierp:print-done', handlePrintDone);
  }, []);

  // Focus automático en input de producto al terminar la carga inicial
  useEffect(() => {
    if (!cargando && inputProductoRef.current) {
      setTimeout(() => {
        inputProductoRef.current?.focus?.();
      }, 200);
    }
  }, [cargando]);

  useEffect(() => {
    return () => {
      if (resolverConfirmacionListaRef.current) {
        resolverConfirmacionListaRef.current(false);
        resolverConfirmacionListaRef.current = null;
      }
    };
  }, []);

  // === TICKETS ===
  const crearNuevoTicket = () => {
    if (tickets.length >= 10) {
      toast.warning('Máximo 10 tickets simultáneos');
      return;
    }
    
    const nuevoId = Math.max(...tickets.map(t => t.id)) + 1;
    const nuevoTicket = {
      id: nuevoId,
      nombre: `Ticket ${nuevoId}`,
      productos: [],
      totales: { subtotal: 0, itbis: 0, total: 0 },
      clienteSeleccionado: clienteDefecto?.id || null,
      nombreCliente: clienteDefecto?.nombre || '',
      id_lista_precio: clienteDefecto?.id_lista_precio ?? null,
      tipoComprobanteFiscal: defaultTipoComprobante,
      comentario: '',
      fecha_hora_creacion: obtenerFechaHoraActualLocal(),
      fecha_emision: obtenerFechaActualLocal(),
      id_centro_costo: null,
      tipoPago: 'contado',
      metodosPago: [{ metodoPago: '01', monto: '' }],
      abonoInicial: null,
      id_factura_editando: null,
      productosOriginales: null,
      id_entidad_cliente_original: null
    };

    setTickets([...tickets, nuevoTicket]);
    setTicketActivo(tickets.length);
    toast.success(`${nuevoTicket.nombre} creado`);
  };

  const cerrarTicket = (index) => {
    const ticket = tickets[index];
    
    if (ticket.productos.length > 0) {
      if (!window.confirm(`${ticket.nombre} tiene productos. ¿Seguro que deseas cerrarlo?`)) {
        return;
      }
    }
    
    if (tickets.length === 1) {
      setTickets([{
        ...ticket,
        productos: [],
        totales: { subtotal: 0, itbis: 0, total: 0 },
        clienteSeleccionado: clienteDefecto?.id || null,
        nombreCliente: clienteDefecto?.nombre || '',
        id_lista_precio: clienteDefecto?.id_lista_precio ?? null,
        tipoComprobanteFiscal: defaultTipoComprobante,
        comentario: '',
        fecha_hora_creacion: obtenerFechaHoraActualLocal(),
        fecha_emision: obtenerFechaActualLocal(),
        id_centro_costo: null,
        tipoPago: 'contado',
        metodosPago: [{ metodoPago: '01', monto: '' }],
        abonoInicial: null,
        id_factura_editando: null,
        productosOriginales: null,
        id_entidad_cliente_original: null
      }]);
      toast.info('Ticket limpiado');
      return;
    }
    
    const nuevosTickets = tickets.filter((_, i) => i !== index);
    setTickets(nuevosTickets);
    
    if (ticketActivo >= nuevosTickets.length) {
      setTicketActivo(nuevosTickets.length - 1);
    }
    
    toast.success(`${ticket.nombre} cerrado`);
  };

  const limpiarTicketActivo = () => {
    const ticket = tickets[ticketActivo];
    const nuevosTickets = [...tickets];
    nuevosTickets[ticketActivo] = {
      ...ticket,
      productos: [],
      totales: { subtotal: 0, itbis: 0, total: 0 },
      clienteSeleccionado: clienteDefecto?.id || null,
      nombreCliente: clienteDefecto?.nombre || '',
      id_lista_precio: clienteDefecto?.id_lista_precio ?? null,
      tipoComprobanteFiscal: defaultTipoComprobante,
      comentario: '',
      fecha_hora_creacion: obtenerFechaHoraActualLocal(),
      fecha_emision: obtenerFechaActualLocal(),
      id_centro_costo: null,
      tipoPago: 'contado',
      metodosPago: [{ metodoPago: '01', monto: '' }],
      abonoInicial: null,
      id_factura_editando: null,
      productosOriginales: null,
      id_entidad_cliente_original: null
    };
    setTickets(nuevosTickets);

    setTimeout(() => {
      inputProductoRef.current?.limpiarYFocus?.();
    }, 300);
  };

  // === CLIENTES ===
  const obtenerPrecioProductoDesdeAPI = async (idProducto, idListaPrecio) => {
    const params = {};
    if (idListaPrecio) {
      params.id_lista_precio = idListaPrecio;
    }

    const response = await instanciaAxios.get(
      `/producto/poridoreferencia/${encodeURIComponent(idProducto)}`,
      { params }
    );

    const producto = response.data || {};
    const precioResuelto = parsearMonto(producto.precio);
    const precioBase = parsearMonto(producto.precio_base, precioResuelto);
    const precioLista = producto.precio_lista === null || producto.precio_lista === undefined
      ? null
      : parsearMonto(producto.precio_lista, null);
    const fuentePrecio = producto.fuente_precio || (precioLista !== null ? 'lista' : 'base');

    return {
      precioResuelto,
      precioBase,
      precioLista,
      fuentePrecio
    };
  };

  const recalcularPreciosTicketPorLista = async (indexTicket, idListaPrecioNueva) => {
    const ticketObjetivo = tickets[indexTicket];
    if (!ticketObjetivo?.productos?.length) return;

    const productosARecalcular = ticketObjetivo.productos.filter(p => !p.precioEditado);
    if (productosARecalcular.length === 0) {
      toast.info('Los productos del ticket tienen precio editado manualmente; no se recalcularon.');
      return;
    }

    let cantidadErrores = 0;
    const resultados = await Promise.all(
      productosARecalcular.map(async (productoTicket) => {
        try {
          const precioData = await obtenerPrecioProductoDesdeAPI(productoTicket.id_producto, idListaPrecioNueva);
          return { id_producto: productoTicket.id_producto, ...precioData };
        } catch (error) {
          cantidadErrores += 1;
          console.error(`No se pudo recalcular precio para producto ${productoTicket.id_producto}`, error);
          return null;
        }
      })
    );

    const mapaResultados = new Map(
      resultados
        .filter(Boolean)
        .map((item) => [item.id_producto, item])
    );

    setTickets(prev => {
      const nuevos = [...prev];
      const ticket = nuevos[indexTicket];
      if (!ticket) return prev;

      ticket.productos = ticket.productos.map((productoTicket) => {
        if (productoTicket.precioEditado) return productoTicket;
        const precioRecalculado = mapaResultados.get(productoTicket.id_producto);
        if (!precioRecalculado) return productoTicket;

        return {
          ...productoTicket,
          precio: precioRecalculado.precioResuelto,
          precioOriginal: precioRecalculado.precioResuelto,
          precioBase: precioRecalculado.precioBase,
          precioLista: precioRecalculado.precioLista,
          fuentePrecio: precioRecalculado.fuentePrecio
        };
      });

      calcularTotales(ticket);
      return nuevos;
    });

    if (cantidadErrores > 0) {
      toast.warning(`Se recalcularon precios con ${cantidadErrores} producto(s) sin actualización.`);
    } else {
      toast.success('Precios del ticket recalculados con la nueva lista del cliente.');
    }
  };

  const solicitarConfirmacionCambioLista = () => {
    setConfirmacionCambioLista({
      mostrar: true,
      titulo: 'Cambio de lista de precios',
      mensaje:
        'Este ticket ya tiene productos y cambió la lista de precio del cliente.\n\n' +
        'Recalcular: actualiza precios de líneas NO editadas manualmente.\n' +
        'Mantener: conserva precios actuales y aplica la nueva lista solo a productos nuevos.',
      textoConfirmar: 'Recalcular',
      textoCancelar: 'Mantener',
      tipo: 'info'
    });

    return new Promise((resolve) => {
      resolverConfirmacionListaRef.current = resolve;
    });
  };

  const resolverConfirmacionCambioLista = (recalcular) => {
    setConfirmacionCambioLista(prev => ({ ...prev, mostrar: false }));
    if (resolverConfirmacionListaRef.current) {
      resolverConfirmacionListaRef.current(recalcular);
      resolverConfirmacionListaRef.current = null;
    }
  };

  const confirmarCambioListaCliente = () => resolverConfirmacionCambioLista(true);
  const cancelarCambioListaCliente = () => resolverConfirmacionCambioLista(false);

  const cargarDatosCliente = async (id_entidad, indexTicket, opciones = {}) => {
    const {
      confirmarCambioLista = false,
      mostrarWarningSinComprobante = true
    } = opciones;

    try {
      const response = await instanciaAxios.get(`/entidad/${id_entidad}`);
      const cliente = response.data;

      const ticketActualSnapshot = tickets[indexTicket];
      const idListaPrecioAnterior = normalizarIdListaPrecio(ticketActualSnapshot?.id_lista_precio);
      const idListaPrecioNueva = normalizarIdListaPrecio(cliente.detallesCliente?.id_lista_precio);
      const huboCambioLista = idListaPrecioAnterior !== idListaPrecioNueva;
      const tieneProductos = (ticketActualSnapshot?.productos?.length || 0) > 0;

      let tipoComprobanteFiscal = '';
      if (cliente.detallesCliente?.id_tipo_comprobante_fiscal) {
        tipoComprobanteFiscal = cliente.detallesCliente.id_tipo_comprobante_fiscal.toString();
      } else if (mostrarWarningSinComprobante) {
        toast.warning('El cliente no tiene tipo de comprobante fiscal registrado');
      }

      let recalcularPrecios = false;
      if (confirmarCambioLista && tieneProductos && huboCambioLista) {
        recalcularPrecios = await solicitarConfirmacionCambioLista();
      }

      setTickets(prev => {
        const nuevos = [...prev];
        if (!nuevos[indexTicket]) return prev;
        nuevos[indexTicket] = {
          ...nuevos[indexTicket],
          tipoComprobanteFiscal,
          id_lista_precio: idListaPrecioNueva
        };
        return nuevos;
      });

      if (confirmarCambioLista && tieneProductos && huboCambioLista) {
        if (recalcularPrecios) {
          await recalcularPreciosTicketPorLista(indexTicket, idListaPrecioNueva);
        } else {
          toast.info('Se mantuvieron precios actuales. La nueva lista aplica para productos nuevos.');
        }
      }
    } catch (error) {
      console.error('Error al cargar datos del cliente:', error);
      toast.error('Error al cargar datos del cliente');
    }
  };

  const handleClienteCreado = async (clienteCreado) => {
    const nuevosTickets = [...tickets];
    const nombreCliente = resolverNombreClienteDisplay(clienteCreado);
    nuevosTickets[ticketActivo].clienteSeleccionado = clienteCreado.id_entidad;
    nuevosTickets[ticketActivo].nombreCliente = nombreCliente;
    nuevosTickets[ticketActivo].id_entidad_cliente_original = clienteCreado.id_entidad;
    nuevosTickets[ticketActivo].id_lista_precio = normalizarIdListaPrecio(clienteCreado.id_lista_precio);
    nuevosTickets[ticketActivo].tipoComprobanteFiscal = clienteCreado.id_tipo_comprobante_fiscal?.toString() || defaultTipoComprobante;
    setTickets(nuevosTickets);
    setMostrarModalNuevoCliente(false);

    await cargarDatosCliente(clienteCreado.id_entidad, ticketActivo, {
      confirmarCambioLista: false,
      mostrarWarningSinComprobante: true
    });
  };

  const handleSeleccionarCliente = async (cliente) => {
    const nuevosTickets = [...tickets];
    const nombreCliente = resolverNombreClienteDisplay(cliente);
    nuevosTickets[ticketActivo].clienteSeleccionado = cliente.id_entidad;
    nuevosTickets[ticketActivo].nombreCliente = nombreCliente;
    nuevosTickets[ticketActivo].id_entidad_cliente_original = cliente.id_entidad;
    setTickets(nuevosTickets);
    setMostrarModalClientes(false);

    await cargarDatosCliente(cliente.id_entidad, ticketActivo, {
      confirmarCambioLista: true,
      mostrarWarningSinComprobante: true
    });
  };

  const handleClickBuscarCliente = () => {
    setMostrarModalClientes(true);
  };

  // === PRODUCTOS ===
  const handleProductoSeleccionado = (producto) => {
    if (!producto) return;

    setProductoTemporal(producto);
    setCantidadTemporal(producto.es_pesable ? '' : '1');
    setPrecioTemporal(parsearMonto(producto.precio).toString());
    setModoModal('agregar');
    setIndexProductoEditar(null);
    setModoPrecioTemporal('unitario');
    setTotalTemporalConImpuesto('');

    requestAnimationFrame(() => {
      setMostrarModalCantidad(true);
    });
  };

  // Recalcula precioTemporal (base) a partir de un total con ITBIS incluido
  // para la cantidad actual del modal. Usado por el modo "Total" del modal
  // de producto; la tasa se toma de productoTemporal (misma que aplicará el
  // cálculo final del ticket).
  const actualizarTotalTemporal = (valorTotal) => {
    setTotalTemporalConImpuesto(valorTotal);

    const cantidad = parseFloat(cantidadTemporal) || 0;
    const total = parseFloat(valorTotal);
    if (cantidad <= 0 || !Number.isFinite(total)) return;

    const tasa = obtenerTasaItbisEfectiva(productoTemporal);
    const precioBase = roundTo2(total / cantidad / (1 + tasa / 100));
    setPrecioTemporal(precioBase.toString());
  };

  // Si el usuario cambia la cantidad mientras está en modo "Total", el precio
  // base debe recalcularse para que el total con impuesto se mantenga fijo.
  useEffect(() => {
    if (modoPrecioTemporal !== 'total') return;
    if (totalTemporalConImpuesto === '') return;

    const cantidad = parseFloat(cantidadTemporal) || 0;
    const total = parseFloat(totalTemporalConImpuesto);
    if (cantidad <= 0 || !Number.isFinite(total)) return;

    const tasa = obtenerTasaItbisEfectiva(productoTemporal);
    const precioBase = roundTo2(total / cantidad / (1 + tasa / 100));
    setPrecioTemporal(precioBase.toString());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cantidadTemporal]);

  const handleClickInputConProducto = (producto) => {
    if (producto && productoTemporal) {
      setMostrarModalCantidad(true);
    }
  };

  const agregarProductoAlTicket = () => {
    if (!productoTemporal) return;
    
    const cantidad = parseFloat(cantidadTemporal) || 0;
    if (cantidad <= 0 || cantidadTemporal === '') {
      toast.error('La cantidad debe ser mayor a 0');
      return;
    }

    const precioEditado = parseFloat(precioTemporal);
    if (isNaN(precioEditado) || precioEditado < 0) {
      toast.error('El precio debe ser un número válido');
      return;
    }
    
    const nuevosTickets = [...tickets];
    const ticket = nuevosTickets[ticketActivo];
    
    const precioOriginalProducto = parsearMonto(
      productoTemporal.precioOriginal ?? productoTemporal.precio
    );
    const precioBaseProducto = parsearMonto(
      productoTemporal.precioBase ?? productoTemporal.precio_base ?? productoTemporal.precio,
      precioOriginalProducto
    );
    const precioListaProducto = productoTemporal.precioLista ?? productoTemporal.precio_lista;
    const precioListaNormalizado = precioListaProducto === undefined || precioListaProducto === null
      ? null
      : parsearMonto(precioListaProducto, null);
    const fuentePrecioProducto = productoTemporal.fuentePrecio || productoTemporal.fuente_precio || (
      precioListaNormalizado !== null ? 'lista' : 'base'
    );
    const fueEditadoPrecio = Math.abs(precioEditado - precioOriginalProducto) > 0.001;

    if (modoModal === 'editar') {
      if (indexProductoEditar !== null) {
        ticket.productos[indexProductoEditar].cantidad = cantidad;
        ticket.productos[indexProductoEditar].precio = precioEditado;
        ticket.productos[indexProductoEditar].precioEditado = fueEditadoPrecio;
        if (!ticket.productos[indexProductoEditar].precioOriginal) {
          ticket.productos[indexProductoEditar].precioOriginal = precioOriginalProducto;
        }
        ticket.productos[indexProductoEditar].precioBase = precioBaseProducto;
        ticket.productos[indexProductoEditar].precioLista = precioListaNormalizado;
        ticket.productos[indexProductoEditar].fuentePrecio = fuentePrecioProducto;
        toast.success('Producto actualizado');
      }
    } else {
      // "Last price wins": always merge by id_producto regardless of price
      const indexExistente = ticket.productos.findIndex(
        p => p.id_producto === productoTemporal.id_producto
      );
      
      if (indexExistente >= 0) {
        // Merge: sum quantities, use latest price
        ticket.productos[indexExistente].cantidad += cantidad;
        ticket.productos[indexExistente].precio = precioEditado;
        ticket.productos[indexExistente].precioEditado = fueEditadoPrecio || ticket.productos[indexExistente].precioEditado;
        ticket.productos[indexExistente].precioBase = precioBaseProducto;
        ticket.productos[indexExistente].precioLista = precioListaNormalizado;
        ticket.productos[indexExistente].fuentePrecio = fuentePrecioProducto;
        if (fueEditadoPrecio) {
          toast.success('Cantidad y precio actualizados');
        } else {
          toast.success('Cantidad actualizada');
        }
      } else {
        const indicador = productoTemporal.indicador_facturacion || 1;
        
        let tasaITBIS = 18;
        if (indicador === 2) tasaITBIS = 16;
        else if ([3, 4, 5].includes(indicador)) tasaITBIS = 0;
        
        if (productoTemporal.itbis_tasa !== undefined && productoTemporal.itbis_tasa !== null) {
          tasaITBIS = productoTemporal.itbis_tasa;
        }
        
        const nuevoProducto = {
          id_producto: productoTemporal.id_producto,
          descripcion: productoTemporal.descripcion,
          precio: precioEditado,
          precioOriginal: precioOriginalProducto,
          precioBase: precioBaseProducto,
          precioLista: precioListaNormalizado,
          fuentePrecio: fuentePrecioProducto,
          precioEditado: fueEditadoPrecio,
          cantidad: cantidad,
          indicador_facturacion: indicador,
          itbis_tasa: tasaITBIS,
          id_tipo_producto: productoTemporal.id_tipo_producto || 1
        };
        ticket.productos.push(nuevoProducto);
        toast.success('Producto agregado');
      }
    }
    
    calcularTotales(ticket);
    setTickets(nuevosTickets);
    
    setMostrarModalCantidad(false);
    setProductoTemporal(null);
    setCantidadTemporal('1');
    setPrecioTemporal('');
    setModoModal('agregar');
    setIndexProductoEditar(null);
    setModoPrecioTemporal('unitario');
    setTotalTemporalConImpuesto('');

    setTimeout(() => {
      inputProductoRef.current?.limpiarYFocus?.();
    }, 100);
  };

  const eliminarProducto = (indexProducto) => {
    const nuevosTickets = [...tickets];
    const ticket = nuevosTickets[ticketActivo];
    
    ticket.productos.splice(indexProducto, 1);
    calcularTotales(ticket);
    setTickets(nuevosTickets);
    
    toast.info('Producto eliminado');
  };

  const editarCantidadProducto = (indexProducto) => {
    const ticket = tickets[ticketActivo];
    const producto = ticket.productos[indexProducto];
    
    setProductoTemporal(producto);
    setCantidadTemporal(producto.cantidad.toString());
    setPrecioTemporal(parseFloat(producto.precio || 0).toString());
    setModoModal('editar');
    setIndexProductoEditar(indexProducto);
    setModoPrecioTemporal('unitario');
    setTotalTemporalConImpuesto('');
    setMostrarModalCantidad(true);
  };

  // Agregar producto rápido (barcode scan / Enter en referencia): sin modal.
  // Para productos pesables con peso embebido en el barcode, usa ese peso como cantidad.
  const agregarProductoRapido = (producto) => {
    if (!producto) return;

    // Pesable sin peso embebido → abrir modal de cantidad (para digitar o capturar de balanza)
    if (producto.es_pesable && !(producto.peso_embebido > 0)) {
      handleProductoSeleccionado(producto);
      return;
    }

    const nuevosTickets = [...tickets];
    const ticket = nuevosTickets[ticketActivo];

    const esPesableConPeso = producto.es_pesable && producto.peso_embebido > 0;
    const cantidadNueva = esPesableConPeso ? producto.peso_embebido : 1;

    const indexExistente = ticket.productos.findIndex(
      p => p.id_producto === producto.id_producto
    );

    if (indexExistente >= 0) {
      if (esPesableConPeso) {
        // Pesable: acumular el peso del nuevo scan al ya existente en el ticket
        ticket.productos[indexExistente].cantidad = roundTo2(
          ticket.productos[indexExistente].cantidad + producto.peso_embebido
        );
        toast.success(`+${producto.peso_embebido}kg ${producto.descripcion}`);
      } else {
        ticket.productos[indexExistente].cantidad += 1;
        toast.success(`+1 ${producto.descripcion}`);
      }
    } else {
      const indicador = producto.indicador_facturacion || 1;
      let tasaITBIS = 18;
      if (indicador === 2) tasaITBIS = 16;
      else if ([3, 4, 5].includes(indicador)) tasaITBIS = 0;
      if (producto.itbis_tasa !== undefined && producto.itbis_tasa !== null) {
        tasaITBIS = producto.itbis_tasa;
      }

      const precioResuelto = parsearMonto(producto.precio);
      const precioBase = parsearMonto(producto.precio_base ?? producto.precio, precioResuelto);
      const precioLista = producto.precio_lista === undefined || producto.precio_lista === null
        ? null
        : parsearMonto(producto.precio_lista, null);
      const fuentePrecio = producto.fuente_precio || (precioLista !== null ? 'lista' : 'base');

      ticket.productos.push({
        id_producto: producto.id_producto,
        descripcion: producto.descripcion,
        precio: precioResuelto,
        precioOriginal: precioResuelto,
        precioBase,
        precioLista,
        fuentePrecio,
        precioEditado: false,
        cantidad: cantidadNueva,
        indicador_facturacion: indicador,
        itbis_tasa: tasaITBIS,
        id_tipo_producto: producto.id_tipo_producto || 1
      });
      const mensajeAgregado = esPesableConPeso
        ? `${producto.descripcion} (${producto.peso_embebido}kg) agregado`
        : `${producto.descripcion} agregado`;
      toast.success(mensajeAgregado);
    }

    calcularTotales(ticket);
    setTickets(nuevosTickets);
  };

  // === CÁLCULOS ===
  const calcularImpuestoPorProducto = (producto) => {
    const subtotal = roundTo2(producto.precio * producto.cantidad);
    if ([1, 2, 3].includes(producto.indicador_facturacion)) {
      return roundTo2(subtotal * (producto.itbis_tasa / 100));
    }
    return 0;
  };

  const calcularTotales = (ticket) => {
    const subtotal = ticket.productos.reduce(
      (acc, p) => acc + roundTo2(p.precio * p.cantidad),
      0
    );
    const itbis = ticket.productos.reduce(
      (acc, p) => acc + calcularImpuestoPorProducto(p),
      0
    );
    ticket.totales = {
      subtotal: roundTo2(subtotal),
      itbis: roundTo2(itbis),
      total: roundTo2(subtotal + itbis)
    };
  };

  // === BÚSQUEDA DE FACTURAS ===
  const buscarFacturas = async (filtrosActuales) => {
    const filtros = filtrosActuales || filtrosBusqueda;
    try {
      setCargandoFacturas(true);
      const params = { limite: 50 };
      if (filtros.busqueda) params.busqueda = filtros.busqueda;
      if (filtros.fecha_desde) params.fecha_desde = filtros.fecha_desde;
      if (filtros.fecha_hasta) params.fecha_hasta = filtros.fecha_hasta;

      const response = await instanciaAxios.get('/factura', { params });
      setFacturasBuscadas(response.data?.facturas || response.data || []);
    } catch (error) {
      console.error('Error al buscar facturas:', error);
      toast.error('Error al buscar facturas');
    } finally {
      setCargandoFacturas(false);
    }
  };

  const handleSeleccionarFacturaBuscada = (factura) => {
    setIdFacturaPDF(factura.id_factura);
    setMostrarModalBusquedaFacturas(false);
    setMostrarPDFFactura(true);
  };

  const handleBotonBuscar = () => {
    setMostrarModalBusquedaFacturas(true);
    buscarFacturas();
  };

  const limpiarFiltrosBusqueda = () => {
    setFiltrosBusqueda({
      busqueda: '',
      fecha_desde: '',
      fecha_hasta: ''
    });
  };

  // Ticket activo helper
  const ticketActual = tickets[ticketActivo] || tickets[0];

  return {
    // State
    tickets, setTickets,
    ticketActivo, setTicketActivo,
    ticketActual,
    sucursales, setSucursales,
    sucursalSeleccionada, setSucursalSeleccionada,
    centrosCosto,
    mostrarCamposAdicionales, setMostrarCamposAdicionales,
    mostrarModalClientes, setMostrarModalClientes,
    cargando,
    procesandoCobro, setProcesandoCobro,
    tiposComprobantesFiscales,
    mostrarModalNuevoCliente, setMostrarModalNuevoCliente,
    mostrarModalCantidad, setMostrarModalCantidad,
    productoTemporal, setProductoTemporal,
    cantidadTemporal, setCantidadTemporal,
    precioTemporal, setPrecioTemporal,
    modoModal, setModoModal,
    modoPrecioTemporal, setModoPrecioTemporal,
    totalTemporalConImpuesto, actualizarTotalTemporal,
    indexProductoEditar, setIndexProductoEditar,
    mostrarModalPago, setMostrarModalPago,
    mostrarPDFFactura, setMostrarPDFFactura,
    pdfBase64, setPdfBase64,
    facturaCreada, setFacturaCreada,
    metodosPago,
    clienteDefecto,
    pasarelaDefecto,
    sucursalDefecto,
    mostrarModalBusquedaFacturas, setMostrarModalBusquedaFacturas,
    facturasBuscadas,
    cargandoFacturas,
    idFacturaPDF, setIdFacturaPDF,
    filtrosBusqueda, setFiltrosBusqueda,
    confirmacionCambioLista,
    inputProductoRef,
    inputCantidadRef,
    // Ticket functions
    crearNuevoTicket,
    cerrarTicket,
    limpiarTicketActivo,
    // Client functions
    cargarDatosCliente,
    handleClienteCreado,
    handleSeleccionarCliente,
    handleClickBuscarCliente,
    confirmarCambioListaCliente,
    cancelarCambioListaCliente,
    // Product functions
    handleProductoSeleccionado,
    handleClickInputConProducto,
    agregarProductoAlTicket,
    agregarProductoRapido,
    eliminarProducto,
    editarCantidadProducto,
    // Calculation functions
    calcularImpuestoPorProducto,
    calcularTotales,
    obtenerTasaItbisEfectiva,
    // Search functions
    buscarFacturas,
    handleSeleccionarFacturaBuscada,
    handleBotonBuscar,
    limpiarFiltrosBusqueda
  };
}

